<template>
  <div
    :class="[
      'milestone-overview-circle-container',
      {
        'last-milestone': last,
        active,
        completed,
      },
    ]"
    :style="containerStyle"
  >
    <div
      :class="[
        'milestone-overview-circle',
        {
          active,
          completed,
        },
      ]"
    >
      <div
        v-tooltip="{
          content: t('map_open_milestone_tooltip'),
          delay: { show: 300, hide: 100 },
          placement: Placement.Top,
          distance: 12,
        }"
        :class="[
          'number-container',
          {
            today: isDeadlineToday && !completed,
          },
        ]"
        @click="$emit('scroll-to-milestone', milestone.id)"
      >
        <div v-if="isDeadlineToday && !completed">
          <FriedIcon icon="alert-circle" />
        </div>
        <FriedH5 v-else>{{ milestoneNumber }}</FriedH5>
      </div>
      <template v-if="showDetails">
        <svg
          v-if="!last"
          class="progress-lines"
          height="20"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            v-if="milestoneProgressPercentage > 0"
            :stroke-dasharray="strokeDashArray"
            x1="0"
            y1="10"
            :x2="xPosition"
            y2="10"
            :class="{
              'green-line': firstActive || completed,
              'start-line': active || completed,
              'end-line': !active && !completed,
            }"
          ></line>
          <line
            :stroke-dasharray="strokeDashArray"
            :x1="xPosition"
            y1="10"
            :x2="width"
            y2="10"
            class="end-line"
          ></line>
          <circle
            v-if="showProgressCircle"
            :cx="xPosition"
            cy="10"
            r="5"
            class="progress-circle"
          ></circle>
        </svg>
        <TransitionFadeInOut>
          <svg
            v-if="isXSmallScreenAndUp || showOnMobile"
            class="down"
            height="30"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line x1="1rem" y1="0" x2="1rem" y2="1rem" class="down-line"></line>
          </svg>
        </TransitionFadeInOut>
      </template>
    </div>
    <TransitionFadeInOut>
      <TasksPopper
        v-if="showDetails && (isXSmallScreenAndUp || showOnMobile)"
        :milestone="milestone"
        :is-deadline-today="isDeadlineToday"
        :farthest-uncompleted-task="farthestUncompletedTask"
        :milestone-active="active"
        :milestone-completed="completed"
      />
    </TransitionFadeInOut>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { Placement, TransitionFadeInOut } from '@getaccept/fried-tofu';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import type { Milestone } from '../types/action-plan';
import MilestoneHelper from '../helpers/milestone.helper';
import TasksPopper from './TasksPopper.vue';

export default defineComponent({
  components: {
    TransitionFadeInOut,
    TasksPopper,
  },
  props: {
    milestone: {
      type: Object as PropType<Milestone>,
      required: true,
    },
    milestoneNumber: {
      type: Number,
      required: true,
    },
    firstActive: {
      type: Boolean,
    },
    active: {
      type: Boolean,
    },
    completed: {
      type: Boolean,
    },
    last: {
      type: Boolean,
    },
    showDetails: {
      type: Boolean,
      default: true,
    },
    milestoneOverviewContainer: {
      type: HTMLDivElement as PropType<HTMLDivElement>,
      default: null,
    },
    totalMilestoneCount: {
      type: Number,
      default: 0,
    },
    width: {
      type: Number,
      default: 0,
    },
    focused: {
      type: Boolean,
    },
  },
  emits: ['scroll-to-milestone'],
  setup(props) {
    const { t } = useI18n();
    const milestoneProgressPercentage = computed(() =>
      MilestoneHelper.getMilestoneProgressPercentage(props.milestone)
    );
    const farthestUncompletedTask = computed(() =>
      MilestoneHelper.getFarthestUncompletedTask(props.milestone, true)
    );
    const isDeadlineToday = computed(() => {
      if (!farthestUncompletedTask.value) {
        return null;
      }

      return DateTime.fromMillis(farthestUncompletedTask.value.deadline).hasSame(
        DateTime.local(),
        'day'
      );
    });
    const { isXXSmallScreen, isXSmallScreenAndUp } = useBreakpoints();

    const containerStyle = computed(() => {
      if (!props.showDetails) {
        return;
      }

      return {
        width: `${props.width}px`,
        'min-width': `${props.width}px`,
      };
    });

    const showProgressCircle = computed(
      () =>
        !props.completed &&
        props.active &&
        props.firstActive &&
        milestoneProgressPercentage.value > 0 &&
        milestoneProgressPercentage.value < 100
    );

    const showOnMobile = computed(() => isXXSmallScreen.value && props.focused);

    const strokeDashArray = computed(() => (props.completed ? 'none' : '5, 5'));

    const xPosition = computed(() => (props.width * milestoneProgressPercentage.value) / 100);

    return {
      milestoneProgressPercentage,
      t,
      Placement,
      isDeadlineToday,
      farthestUncompletedTask,
      isXXSmallScreen,
      isXSmallScreenAndUp,
      containerStyle,
      showProgressCircle,
      showOnMobile,
      strokeDashArray,
      xPosition,
    };
  },
});
</script>

<style lang="scss" scoped>
.milestone-overview-circle-container {
  display: flex;
  align-items: center;
  justify-items: center;
  flex-direction: column;
  text-align: center;

  .milestone-overview-circle {
    width: 2rem;
    height: 2rem;
    text-align: center;
    border-radius: 50%;
    background: var(--gray-98);
    margin: var(--spacing-50);
    box-shadow: var(--shadow-1);
    position: relative;
    transition: all 0.2s;

    .number-container {
      width: 100%;
      height: 100%;
      text-align: center;
      border-radius: 50%;
      background: var(--gray-98);
      box-shadow: var(--shadow-1);
      z-index: 1;
      position: relative;
      cursor: pointer;
      transition: all 0.2s;

      h5 {
        line-height: 2rem;
      }

      div:first-of-type {
        height: 100%;
        display: flex;
        justify-content: center;
      }
    }

    &.completed {
      background: var(--jade-35);
      box-shadow: var(--shadow-1);

      .number-container {
        background: var(--jade-35);
        box-shadow: var(--shadow-1);

        div:first-of-type {
          svg {
            color: var(--white);
          }
        }

        &:hover {
          background: var(--green-25);
        }

        h5 {
          color: var(--white);
        }
      }
    }

    &.active {
      background: var(--blue-60);
      box-shadow: var(--shadow-3);

      /* stylelint-disable-next-line no-descending-specificity */
      .number-container {
        background: var(--blue-60);
        box-shadow: var(--shadow-3);

        div:first-of-type {
          svg {
            color: var(--white);
          }
        }

        &:hover {
          background: var(--blue-45);
          transform: scale(1.3);

          .svg-icon {
            color: var(--red-95);
          }
        }

        h5 {
          color: var(--white);
        }
      }
    }

    .progress-lines {
      position: absolute;
      top: 0.5rem;
      left: 1.5rem;
      margin-top: -2px;

      .start-line,
      .end-line {
        stroke-linecap: round;
        stroke-width: 2px;
        transition: all 0.3s ease-in-out;
        stroke: var(--gray-94);
      }

      .start-line.green-line {
        stroke: var(--jade-35);
      }

      .progress-circle {
        fill: var(--blue-45);
        filter: drop-shadow(var(--shadow-1));
        transition: all 0.3s ease-in-out;
      }
    }

    .down {
      width: 100%;

      .down-line {
        stroke-linecap: round;
        stroke-width: 2px;
        stroke: var(--gray-80);
      }
    }
  }
}
</style>
